import React, { useState } from 'react';
// Native MUI Components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Grid,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Traveller, Trip } from '@/types';
import { Address } from '@/types/address';
import { travellerService } from '@/services/travellerService';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/hooks/useI18n';

interface AddTravellerFormProps {
  trip: Trip;
  trigger: React.ReactNode;
  onTravellerAdded?: () => void;
}

const AddTravellerForm: React.FC<AddTravellerFormProps> = ({ 
  trip,
  trigger,
  onTravellerAdded
}) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const { t } = useI18n();
  const [formData, setFormData] = useState<Omit<Traveller, 'id'>>({
    name: '',
    email: '',
    phone: '',
    address: { street: '', city: '', postalCode: '', country: '' },
    dateOfBirth: '',
    isSkiClubMember: false,
    status: 'pending',
    apartmentId: undefined,
    skiPassType: undefined,
    transportationType: undefined,
  });

  const handleInputChange = (
    field: keyof Omit<Traveller, 'id'>, 
    value: string | boolean | number | Address
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field: keyof Address, value: string) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.dateOfBirth || !formData.phone || !formData.address?.street || !formData.address?.city || !formData.address?.postalCode || !formData.address?.country) {
      toast({
        title: t('traveller.form.missingFields'),
        description: t('traveller.form.missingFieldsDescription'),
        variant: "destructive",
      });
      return;
    }

    try {
      await travellerService.addTraveller(trip.id, {
        ...formData,
        status: 'confirmed'
      });

      toast({
        title: t('traveller.form.travellerAdded'),
        description: t('traveller.form.travellerAddedDescription', { name: formData.name }),
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: { street: '', city: '', postalCode: '', country: '' },
        dateOfBirth: '',
        isSkiClubMember: false,
        status: 'pending',
        apartmentId: undefined,
        skiPassType: undefined,
        transportationType: undefined,
      });

      setOpen(false);
      onTravellerAdded?.();
    } catch (error) {
      console.error("Error adding traveller:", error);
      toast({
        title: t('traveller.form.errorAddingTraveller'),
        description: t('traveller.form.errorAddingTravellerDescription'),
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Box onClick={() => setOpen(true)} sx={{ cursor: 'pointer' }}>
        {trigger}
      </Box>

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          <Typography variant="h6" component="div">
            {t('traveller.form.addNewTraveller')}
          </Typography>
          <IconButton
            aria-label={t('common.close')}
            onClick={() => setOpen(false)}
            sx={{
              color: 'grey.500',
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('traveller.form.name')}
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder={t('traveller.form.enterFullName')}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('traveller.form.email')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder={t('traveller.form.enterEmail')}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('traveller.form.phone')}
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder={t('traveller.form.enterPhone')}
                  required
                />
              </Grid>

              {/* Address Section */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                  {t('traveller.form.address')}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('traveller.form.street')}
                  value={formData.address.street}
                  onChange={(e) => handleAddressChange('street', e.target.value)}
                  placeholder={t('traveller.form.streetPlaceholder')}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('traveller.form.postalCode')}
                  value={formData.address.postalCode}
                  onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                  placeholder={t('traveller.form.postalCodePlaceholder')}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('traveller.form.city')}
                  value={formData.address.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                  placeholder={t('traveller.form.cityPlaceholder')}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('traveller.form.country')}
                  value={formData.address.country}
                  onChange={(e) => handleAddressChange('country', e.target.value)}
                  placeholder={t('traveller.form.countryPlaceholder')}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('traveller.form.dateOfBirth')}
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isSkiClubMember}
                      onChange={(e) => handleInputChange('isSkiClubMember', e.target.checked)}
                    />
                  }
                  label={t('traveller.form.skiClubMember')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('traveller.form.membershipFeeOverride')}
                  type="number"
                  value={formData.membershipFee ?? ''}
                  onChange={(e) => handleInputChange('membershipFee', e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder={t('traveller.form.membershipFeeOverridePlaceholder')}
                  helperText={t('traveller.form.membershipFeeOverrideHelp', { defaultFee: trip.defaultMembershipFee })}
                  inputProps={{ min: 0, step: 1.00 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setOpen(false)} 
            variant="outlined"
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
          >
            {t('traveller.form.addTraveller')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddTravellerForm;
